import React from 'react';
import styled from 'styled-components';
import {IComment} from '../../model/commentModel';
import {useTranslation} from 'react-i18next';
import CommentInput from './CommentInput';
import {IWorkerState} from '../../model/workerState';
import NonEditingCommentFooter from './NonEditingCommentFooter';
import {ILike} from '../../model/reactionModel';
import config from '../../service/config';
import Linkify from 'react-linkify';
import {TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import dayjs, {UnitType} from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {AriaAlert} from '../util/StyledElements';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// Need these values because dayJS showing some comment dates a few seconds in the future when a comment is first submitted
const offsetCount = 5;
const offsetUnit: UnitType = 'second';

const CommentBodyWrapper = styled.div<{isReply: boolean; isEditing: boolean}>`
   padding-right: ${props => props.isEditing ? '0' : '0.5rem'};
   padding-left: ${props => props.isReply ? '0.375rem' : '1.875rem'};
   font-weight: 400;
   margin-top: ${props => props.isReply ? '0.25rem' : '0.75rem'};
   word-break: break-word;
   white-space: pre-wrap;

   a {
      color: inherit;
      text-decoration: underline;
      border: 1px dashed transparent;
      outline: none;

      &:focus, &:hover {
         text-decoration: none;
         border-color: currentColor;
      }

      &:active {
         text-decoration: none;
         border-color: currentColor;
         border-style: solid;
      }
   }
`;

const CommentDateWrapper = styled.div<{theme: TscThemeNames}>`
   margin-top: 0.5rem;
   font-size: 0.8125rem;
   font-style: italic;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dawn.dark : TscStyles.color.text.mediumDark};
`;

export const CommentBodyBase: React.FC<ICommentBodyProps & IStateMappedProps> = ({comment, isReply, loadingState, isCommentOwner, setDeleteAnnotationOnSave, saveEdit, handleExpandReplies, setShowDeleteAlertModal, deleteAnnotationOnSave, theme}) => {
   const {t} = useTranslation();
   const showEditedLabel: boolean = (!comment.isEditing || !isCommentOwner) && !comment.isDeleted && !!comment.lastModifiedDate;
   const userHasLikedComment = comment.likes && comment.likes.some((like: ILike) => like.UserId === config.user.TechSmithId);

   const getCommentText = () => {
      if (comment.text) {
         return comment.text;
      } else if (comment.annotation) {
         return t('conversations.annotationAttached');
      }
      return t('conversations.commentDeletedText');
   };

   return (
      <CommentBodyWrapper isReply={isReply} isEditing={comment.isEditing} data-test-id="conversation-comment-body">
         {(!comment.isEditing || !isCommentOwner) &&
         <Linkify
            componentDecorator={(decoratedHref, decoratedText, key) => (
               <a target="blank" href={decoratedHref} key={key} rel="noopener noreferrer nofollow">
                  {decoratedText}
               </a>)}
         >
            {getCommentText()}
         </Linkify>}
         {!comment.isEditing && <CommentDateWrapper theme={theme} title={`${dayjs.utc(comment.createDate).format('LLLL')} UTC`}>
            {showEditedLabel && t('conversations.commentEditedText')}
            {dayjs(showEditedLabel ? comment.lastModifiedDate : comment.createDate).subtract(offsetCount, offsetUnit).fromNow()}
         </CommentDateWrapper>}
         {comment.isEditing && <CommentInput inputId={`${comment.id}-input`} hasAnnotation={false} setDeleteAnnotationOnSave={setDeleteAnnotationOnSave} isCommentOwner={isCommentOwner} isReply={isReply} comment={comment} onSubmit={(editedText: string) => saveEdit(editedText)} loadingState={loadingState} setShowDeleteAlertModal={setShowDeleteAlertModal} deleteAnnotationOnSave={deleteAnnotationOnSave} />}
         {!comment.isEditing && <NonEditingCommentFooter userHasLikedComment={userHasLikedComment} comment={comment} isReply={isReply} handleExpandReplies={handleExpandReplies} />}
         <AriaAlert role="alert" id="comment-editing-alert">{t('conversations.editCommentClickedAlert')}</AriaAlert>
      </CommentBodyWrapper>
   );
};

export interface ICommentBodyProps {
   comment: IComment;
   isReply: boolean;
   loadingState: IWorkerState;
   deleteAnnotationOnSave: boolean;
   isCommentOwner: boolean;
   setDeleteAnnotationOnSave(deleteAnnotationOnSave: boolean): void;
   handleExpandReplies(): void;
   saveEdit(editedText: string): void;
   setShowDeleteAlertModal(showDeleteAlertModal: boolean): void;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
}

const mapStatesToProps = (
   {comment, isReply, loadingState, deleteAnnotationOnSave, isCommentOwner, setDeleteAnnotationOnSave, handleExpandReplies, saveEdit, setShowDeleteAlertModal}: ICommentBodyProps,
   {theme}: Partial<IStateMappedProps>
): ICommentBodyProps & IStateMappedProps => ({comment, isReply, loadingState, deleteAnnotationOnSave, isCommentOwner, setDeleteAnnotationOnSave, handleExpandReplies, saveEdit, setShowDeleteAlertModal, theme});

export default withMemoizedContexts(mapStatesToProps, themeStore)(CommentBodyBase);
